import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ContentPageModel } from "@solveindustrial/Services/ContentSearchService";
import { customLinkCallToActionStyles } from "@solveindustrial/Utilities/styleguide/link";
import { mediaQueriesBP } from "@solveindustrial/Utilities/variables";
import React from "react";
import { css } from "styled-components";

interface ContentSearchCardProps {
    id?: string;
    page: ContentPageModel;
    showTitle: boolean;
    showDescription: boolean;
    showDate: boolean;
    showImage: boolean;
    maxDescriptionLength: number;
}

export interface ContentSearchCardStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: InjectableCss;
    pageTitle?: TypographyPresentationProps;
    pageDescription?: TypographyPresentationProps;
    pageDate?: TypographyPresentationProps;
    pageLink?: LinkPresentationProps;
    readMore?: LinkPresentationProps;
}

export const contentSearchCardStyles: ContentSearchCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            padding: 24px;
            flex-direction: column;
            gap: 24px;

            ${mediaQueriesBP.minMD} {
                flex-direction: row-reverse;
                align-items: center;
            }
        `,
    },
    contentWrapper: {
        css: css`
            flex: 1;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            min-width: 100%;

            ${mediaQueriesBP.minMD} {
                width: 150px;
                min-width: 150px;
            }
        `,
    },
    image: {
        css: css`
            width: 100%;
            height: auto;
            object-fit: contain;
        `,
    },
    pageTitle: {
        variant: "h3",
        size: 18,
        css: css`
            margin-bottom: 10px;
            text-align: left;

            span {
                font-weight: 700 !important;
            }
        `,
    },
    pageDescription: {
        css: css`
            margin-bottom: 10px;
        `,
    },

    pageDate: {
        color: "text.secondary",
        size: 12,
        css: css`
            margin-top: 10px;
        `,
    },

    readMore: {
        css: css`
            ${customLinkCallToActionStyles}

            margin: 10px 0 0;
        `,
    },
};

const styles = contentSearchCardStyles;

const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
};

const truncateText = (text: string, maxLength: number): string => {
    if (!text) {
        return "";
    }
    const plainText = stripHtmlTags(text);
    if (plainText.length <= maxLength) {
        return plainText;
    }
    return `${plainText.substr(0, maxLength)}...`;
};

const ContentSearchCard: React.FC<ContentSearchCardProps> = ({
    page,
    showTitle,
    showDescription,
    showDate,
    showImage,
    maxDescriptionLength,
}) => {
    const hasImage = showImage && page.thumbnail;

    return (
        <StyledWrapper {...styles.wrapper}>
            {hasImage && (
                <StyledWrapper {...styles.imageWrapper}>
                    <Img
                        {...styles.image}
                        src={page.thumbnail!}
                        altText={page.name}
                        loading="eager"
                        data-test-selector="productDetails_mainImage"
                        tabIndex={0}
                    />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.contentWrapper}>
                {showTitle && (
                    <Typography {...styles.pageTitle}>
                        <Link href={page.link} target="_self">
                            {page.name}
                        </Link>
                    </Typography>
                )}
                {showDescription && page.description && (
                    <Typography {...styles.pageDescription}>
                        {truncateText(page.description, maxDescriptionLength)}
                    </Typography>
                )}
                {/* {showDate && page.publishDate && <Typography {...styles.pageDate}>{page.publishDate}</Typography>} */}
                <br />
                <Link {...styles.readMore} href={page.link} target="_self">
                    {translate("Read More")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ContentSearchCard;
